import { useMemo, useState } from "react";
import { Card } from "antd";
import { toast } from "react-hot-toast";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { useTranslation } from "react-i18next";
import StandardButton from "../components/StandardButton";

const ROMAN_MIN = 1;
const ROMAN_MAX = 3999;

const NUM_TO_ROMAN_TABLE = [
  { value: 1000, symbol: "M" },
  { value: 900, symbol: "CM" },
  { value: 500, symbol: "D" },
  { value: 400, symbol: "CD" },
  { value: 100, symbol: "C" },
  { value: 90, symbol: "XC" },
  { value: 50, symbol: "L" },
  { value: 40, symbol: "XL" },
  { value: 10, symbol: "X" },
  { value: 9, symbol: "IX" },
  { value: 5, symbol: "V" },
  { value: 4, symbol: "IV" },
  { value: 1, symbol: "I" },
];

const ROMAN_VALUES = { I: 1, V: 5, X: 10, L: 50, C: 100, D: 500, M: 1000 };

const numberToRoman = (n) => {
  let remaining = n;
  let result = "";
  for (const { value, symbol } of NUM_TO_ROMAN_TABLE) {
    while (remaining >= value) {
      result += symbol;
      remaining -= value;
    }
  }
  return result;
};

const romanToNumber = (raw) => {
  const s = raw.toUpperCase();
  let total = 0;
  let i = 0;
  while (i < s.length) {
    const cur = ROMAN_VALUES[s[i]];
    const next = ROMAN_VALUES[s[i + 1]];
    if (cur == null) throw new Error(`invalid_char:${s[i]}`);
    if (next != null && cur < next) {
      total += next - cur;
      i += 2;
    } else {
      total += cur;
      i += 1;
    }
  }
  return total;
};

const PRESETS = [
  { labelKey: "roman_numeral_preset_year", value: "2026" },
  { labelKey: "roman_numeral_preset_super_bowl", value: "58" },
  { labelKey: "roman_numeral_preset_chapter", value: "12" },
  { labelKey: "roman_numeral_preset_max", value: "3999" },
];

const RomanNumeralConverter = () => {
  const { t } = useTranslation();
  const [mode, setMode] = useState("num_to_roman");
  const [input, setInput] = useState("2026");
  const [lowercase, setLowercase] = useState(false);

  const result = useMemo(() => {
    const raw = input.trim();
    if (!raw) return { ok: true, empty: true, value: "" };

    if (mode === "num_to_roman") {
      if (!/^-?\d+$/.test(raw)) {
        return { ok: false, errorKey: "roman_numeral_error_not_integer" };
      }
      const n = parseInt(raw, 10);
      if (!Number.isFinite(n)) {
        return { ok: false, errorKey: "roman_numeral_error_not_integer" };
      }
      if (n < ROMAN_MIN || n > ROMAN_MAX) {
        return {
          ok: false,
          errorKey: "roman_numeral_error_out_of_range",
          errorVars: { min: ROMAN_MIN, max: ROMAN_MAX },
        };
      }
      const roman = numberToRoman(n);
      return { ok: true, value: lowercase ? roman.toLowerCase() : roman };
    }

    if (!/^[IVXLCDMivxlcdm]+$/.test(raw)) {
      return { ok: false, errorKey: "roman_numeral_error_invalid_chars" };
    }
    let n;
    try {
      n = romanToNumber(raw);
    } catch {
      return { ok: false, errorKey: "roman_numeral_error_invalid_chars" };
    }
    if (n < ROMAN_MIN || n > ROMAN_MAX) {
      return {
        ok: false,
        errorKey: "roman_numeral_error_out_of_range",
        errorVars: { min: ROMAN_MIN, max: ROMAN_MAX },
      };
    }
    const canonical = numberToRoman(n);
    const nonCanonical = canonical.toUpperCase() !== raw.toUpperCase();
    return {
      ok: true,
      value: String(n),
      warning: nonCanonical
        ? { key: "roman_numeral_warning_non_canonical", vars: { canonical } }
        : null,
    };
  }, [input, mode, lowercase]);

  const copy = async () => {
    if (!result.ok || result.empty || !result.value) {
      toast.error(t("roman_numeral_nothing_to_copy"));
      return;
    }
    try {
      await navigator.clipboard.writeText(result.value);
      toast.success(t("copy_success"));
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  const swap = () => {
    setMode((m) => (m === "num_to_roman" ? "roman_to_num" : "num_to_roman"));
    if (result.ok && !result.empty && result.value) {
      setInput(result.value);
    } else {
      setInput("");
    }
  };

  const clear = () => setInput("");

  const applyPreset = (value) => {
    setMode("num_to_roman");
    setInput(value);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-4">
            <h1 className="text-2xl font-bold text-indigo-700">
              {t("roman_numeral_title")}
            </h1>
            <p className="text-gray-600">{t("roman_numeral_intro")}</p>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">
                {t("roman_numeral_mode_label")}
              </label>
              <div className="flex flex-wrap gap-2">
                <StandardButton
                  onClick={() => setMode("num_to_roman")}
                  type={mode === "num_to_roman" ? "primary" : "secondary"}
                  variant={mode === "num_to_roman" ? undefined : "outlined"}
                  size="small"
                >
                  {t("roman_numeral_mode_num_to_roman")}
                </StandardButton>
                <StandardButton
                  onClick={() => setMode("roman_to_num")}
                  type={mode === "roman_to_num" ? "primary" : "secondary"}
                  variant={mode === "roman_to_num" ? undefined : "outlined"}
                  size="small"
                >
                  {t("roman_numeral_mode_roman_to_num")}
                </StandardButton>
                <StandardButton
                  onClick={swap}
                  type="secondary"
                  variant="outlined"
                  size="small"
                  aria-label={t("roman_numeral_swap_aria")}
                >
                  {t("roman_numeral_swap")}
                </StandardButton>
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <label
                htmlFor="roman-input"
                className="text-sm font-medium text-gray-700"
              >
                {mode === "num_to_roman"
                  ? t("roman_numeral_input_label_number")
                  : t("roman_numeral_input_label_roman")}
              </label>
              <input
                id="roman-input"
                type="text"
                inputMode={mode === "num_to_roman" ? "numeric" : "text"}
                autoComplete="off"
                spellCheck={false}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={
                  mode === "num_to_roman"
                    ? t("roman_numeral_placeholder_number")
                    : t("roman_numeral_placeholder_roman")
                }
                className="w-full p-2 font-mono border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none"
              />
            </div>

            {mode === "num_to_roman" && (
              <div className="bg-indigo-50 p-4 rounded-md border border-indigo-200 flex flex-col gap-3">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={lowercase}
                    onChange={(e) => setLowercase(e.target.checked)}
                    className="h-4 w-4"
                  />
                  {t("roman_numeral_lowercase")}
                </label>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700">
                    {t("roman_numeral_presets_label")}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {PRESETS.map((p) => (
                      <StandardButton
                        key={p.value}
                        onClick={() => applyPreset(p.value)}
                        type="secondary"
                        variant="outlined"
                        size="small"
                      >
                        {t(p.labelKey)}{" "}
                        <span className="font-mono ml-1 text-gray-500">
                          {p.value}
                        </span>
                      </StandardButton>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">
                  {t("roman_numeral_output_label")}
                </label>
                <div className="flex items-center gap-2">
                  <StandardButton
                    onClick={clear}
                    type="secondary"
                    variant="outlined"
                    size="small"
                    disabled={!input}
                  >
                    {t("roman_numeral_clear")}
                  </StandardButton>
                  <StandardButton
                    onClick={copy}
                    type="primary"
                    size="small"
                    disabled={!result.ok || result.empty || !result.value}
                  >
                    {t("copy_button")}
                  </StandardButton>
                </div>
              </div>
              {!result.ok ? (
                <div
                  role="alert"
                  className="w-full min-h-[3rem] p-3 font-mono text-sm bg-red-50 border border-red-200 rounded-md text-red-700"
                >
                  {t(result.errorKey, result.errorVars || {})}
                </div>
              ) : (
                <pre className="w-full min-h-[3rem] p-3 font-mono text-2xl bg-gray-50 border border-gray-200 rounded-md whitespace-pre-wrap break-all text-gray-800">
                  {result.empty || !result.value ? (
                    <em className="text-gray-400 not-italic text-sm">
                      {t("roman_numeral_output_empty")}
                    </em>
                  ) : (
                    result.value
                  )}
                </pre>
              )}
              {result.ok && result.warning && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
                  {t(result.warning.key, result.warning.vars)}
                </div>
              )}
            </div>

            <div className="text-xs text-gray-500 leading-relaxed">
              <p>{t("roman_numeral_note_range")}</p>
              <p>{t("roman_numeral_note_canonical")}</p>
              <p>{t("roman_numeral_note_privacy")}</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default RomanNumeralConverter;
